import { Building2, CreditCard, FileText, TrendingUp, Users, Wrench } from 'lucide-react';
import React from 'react';
import { useQuery } from 'react-query';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const PropertyOwnerDashboard = () => {
  const { user } = useAuth();

  // 대시보드 데이터 가져오기
  const { data: dashboardData, isLoading, error } = useQuery('dashboard', async () => {
    try {
      console.log('🔍 임대사업자 대시보드 데이터 로드 시작');
      const response = await api.get('/dashboard');
      console.log('📊 임대사업자 대시보드 응답:', response);
      return response;
    } catch (error) {
      console.error('❌ 대시보드 데이터 로드 실패:', error);
      throw error;
    }
  });

  // 점유율 계산 (활성 계약 / 총 세대 수)
  const occupancyRate = dashboardData?.totalBuildings > 0 
    ? Math.round((dashboardData?.activeContracts / (dashboardData?.totalBuildings * 10)) * 100) || 0
    : 0;

  const stats = [
    {
      name: '총 건물 수',
      value: dashboardData?.totalBuildings || 0,
      icon: Building2,
      color: 'bg-blue-500',
    },
    {
      name: '총 임차인 수',
      value: dashboardData?.totalTenants || 0,
      icon: Users,
      color: 'bg-green-500',
    },
    {
      name: '활성 계약',
      value: dashboardData?.activeContracts || 0,
      icon: FileText,
      color: 'bg-yellow-500',
    },
    {
      name: '월 수입',
      value: `₩${(dashboardData?.monthlyIncome || 0).toLocaleString()}`,
      icon: CreditCard,
      color: 'bg-purple-500',
    },
    {
      name: '유지보수',
      value: '0', // TODO: 유지보수 데이터 추가
      icon: Wrench,
      color: 'bg-red-500',
    },
    {
      name: '점유율',
      value: `${occupancyRate}%`,
      icon: TrendingUp,
      color: 'bg-indigo-500',
    },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">대시보드 데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">데이터 로드 실패</h2>
          <p className="text-gray-600">대시보드 데이터를 불러올 수 없습니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 헤더 */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            임대사업자 대시보드
          </h1>
          <p className="mt-2 text-gray-600">
            안녕하세요, {user?.name}님! 오늘도 좋은 하루 되세요.
          </p>
        </div>

        {/* 통계 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {stats.map((stat) => (
            <div key={stat.name} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <stat.icon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 최근 청구서 내역 */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">최근 청구서 내역</h3>
          </div>
          <div className="p-6">
            {dashboardData?.billing_summary && dashboardData.billing_summary.length > 0 ? (
              <div className="space-y-4">
                {dashboardData.billing_summary.map((statement) => (
                  <div key={statement.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <div className={`w-2 h-2 rounded-full ${
                          statement.status === 'paid' ? 'bg-green-400' :
                          statement.status === 'pending' ? 'bg-yellow-400' :
                          statement.status === 'overdue' ? 'bg-red-400' : 'bg-gray-400'
                        }`}></div>
                        <span className="text-sm font-medium text-gray-900">
                          {statement.unit_info} - {statement.tenant_name}
                        </span>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          statement.status === 'paid' ? 'bg-green-100 text-green-800' :
                          statement.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          statement.status === 'overdue' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {statement.status === 'paid' ? '납부완료' :
                           statement.status === 'pending' ? '납부대기' :
                           statement.status === 'overdue' ? '연체' : statement.status}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-gray-600">
                        {statement.billing_month} | 청구서 #{statement.statement_number}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">
                        ₩{statement.total_amount.toLocaleString()}
                      </div>
                      {statement.due_date && (
                        <div className="text-xs text-gray-500">
                          납기: {new Date(statement.due_date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 text-4xl mb-4">📄</div>
                <p className="text-gray-600">최근 청구서 내역이 없습니다.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyOwnerDashboard;














